import EyeOutlined from '@ant-design/icons/EyeOutlined'
import { Modal, Tag } from 'antd'
import Table, { ColumnsType } from 'antd/es/table'
import { GeckoMeasurementLabel } from 'features/fde'
import {
  computeSlices,
  ComputeSlicesArgs,
  CtpSlice,
} from 'features/indicationTriage/cards/ffsCalc/calculations/calculateCtp'
import { Sub, Sup } from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useGeckoLocale } from 'features/locale'
import indexOf from 'lodash/indexOf'
import minBy from 'lodash/minBy'
import sortBy from 'lodash/sortBy'
import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

export type Rsf = {
  startIdx: number
  rsf: GeckoMeasurement | undefined
  futureCorrodedThickness: GeckoMeasurement | undefined
  slices: ReadonlyArray<CtpSlice>
}

function renderMeas(meas: GeckoMeasurement | undefined): ReactNode {
  return meas ? (
    <GeckoMeasurementLabel variant='narrow' measurement={meas} />
  ) : (
    <></>
  )
}

function isLowestThicknessRsf(
  rsf: Rsf,
  minThicknessRsf: Rsf | undefined
): boolean {
  return !!minThicknessRsf && rsf.startIdx === minThicknessRsf.startIdx
}

const CtpSliceTable: FC<{ slices: ReadonlyArray<CtpSlice> }> = ({ slices }) => {
  const columns: ColumnsType<CtpSlice> = useMemo(
    () => [
      {
        key: 'si_s',
        dataIndex: 'si_s',
        title: (
          <>
            Si<Sub>s</Sub>
          </>
        ),
        render: renderMeas,
      },
      {
        key: 'si_e',
        dataIndex: 'si_e',
        title: (
          <>
            Si<Sub>e</Sub>
          </>
        ),
        render: renderMeas,
      },
      {
        key: 'si',
        dataIndex: 'si',
        title: (
          <>
            s<Sup>i</Sup>
          </>
        ),
        render: renderMeas,
      },
      {
        key: 'lambda',
        dataIndex: 'lambda',
        title: 'λ',
        render: renderMeas,
      },
      {
        key: 'mt',
        dataIndex: 'mt',
        title: (
          <>
            M<Sub>t</Sub>
          </>
        ),
        render: renderMeas,
      },
      {
        key: 'ai0',
        dataIndex: 'ai0',
        title: (
          <>
            A<Sub>i0</Sub>
          </>
        ),
        render: (val) => <AreaLabel area={val} />,
      },
      {
        key: 'ai',
        dataIndex: 'ai',
        title: (
          <>
            A<Sub>i</Sub>
          </>
        ),
        render: (val) => <AreaLabel area={val} />,
      },
      {
        key: 'rsf',
        dataIndex: 'rsf',
        title: (
          <>
            RSF<Sub>i</Sub>
          </>
        ),
        render: renderMeas,
      },
    ],
    []
  )

  return (
    <Table
      style={{ width: '1000px' }}
      columns={columns}
      dataSource={slices}
      rowKey={({ si_s, si_e }) => [si_s, si_e].join('::')}
      pagination={false}
    />
  )
}

type ComputeRsfsArgs = Pick<
  ComputeSlicesArgs,
  'criticalPath' | 'tc' | 'axis' | 'fca' | 'innerDiameter'
>
export function computeRsfs({
  criticalPath,
  tc,
  axis,
  fca,
  innerDiameter,
}: ComputeRsfsArgs): ReadonlyArray<{
  startIdx: number
  rsf: GeckoMeasurement | undefined
  futureCorrodedThickness: GeckoMeasurement | undefined
  slices: ReadonlyArray<CtpSlice>
}> {
  const rankedStartingPoints = sortBy(
    criticalPath.filter((point) => !!point.value),
    (point) => point.value!.toNumber(WORKING_UNIT_THICKNESS)
  )
  const startIdxs = rankedStartingPoints.map((startingPoint) =>
    indexOf(criticalPath, startingPoint)
  )

  return startIdxs.map((startIdx) => {
    const slices = computeSlices({
      criticalPath,
      startIdx,
      tc,
      axis,
      fca,
      innerDiameter,
    })
    const rsf = minBy(
      slices.map((slice) => slice.rsf),
      (rsf) => rsf.toNumber(GeckoMeasurementUnit.Ratio)
    )
    const futureCorrodedThickness = criticalPath[startIdx]?.value
    return {
      slices,
      rsf,
      startIdx,
      futureCorrodedThickness,
    }
  })
}

const FutureCorrodedThicknessTag: FC<{
  rsf: Rsf
  minThicknessRsf: Rsf | undefined
}> = ({ rsf, minThicknessRsf }) => {
  if (!rsf.futureCorrodedThickness) return <></>
  const isLowest = isLowestThicknessRsf(rsf, minThicknessRsf)

  return (
    <Tag color={isLowest ? CantileverTheme.colors.yellow['800'] : 'default'}>
      <GeckoMeasurementLabel
        variant='narrow'
        measurement={rsf.futureCorrodedThickness}
      />
    </Tag>
  )
}

const AreaLabel: FC<{ area: number }> = ({ area }) => {
  const pref1DUnit = useGeckoLocale().getPreferredMeasurementUnit(
    WORKING_UNIT_THICKNESS
  )
  const convertedNum = Math.pow(
    GeckoMeasurement.CreateFromValue(
      Math.sqrt(area),
      WORKING_UNIT_THICKNESS
    ).toNumber(pref1DUnit),
    2
  )
  return (
    <>
      {convertedNum.toPrecision(3)}
      {pref1DUnit.name}
      <Sup>2</Sup>
    </>
  )
}

export interface RsfTableProps {
  rsfs: ReadonlyArray<Rsf>
}

const ControlsContainer = styled.div`
  display: flex;
  justify-content: center;
  min-width: 50px;
`

export const RsfTable: FC<{ rsfs: ReadonlyArray<Rsf> }> = ({ rsfs }) => {
  const [selectedRsfStartIdx, setSelectedRsfStartIdx] = useState<
    number | undefined
  >(undefined)

  const minThicknessRsf: Rsf | undefined = useMemo(() => {
    return minBy(rsfs, (rsf) =>
      rsf.futureCorrodedThickness?.toNumber(WORKING_UNIT_THICKNESS)
    )
  }, [rsfs])

  const columns = useMemo<ColumnsType<Rsf & { rank: number }>>(() => {
    return [
      {
        key: 'rank',
        title: 'Rank',
        dataIndex: 'rank',
      },
      {
        key: 'futureCorrodedThickness',
        title: (
          <>
            {'t'}
            <Sub>f</Sub>
          </>
        ),
        dataIndex: 'futureCorrodedThickness',
        render: (val, row) => (
          <FutureCorrodedThicknessTag
            rsf={row}
            minThicknessRsf={minThicknessRsf}
          />
        ),
      },
      {
        key: 'rsf',
        title: 'RSF',
        dataIndex: 'rsf',
        render: renderMeas,
      },
      {
        key: 'controls',
        render: (val, row) => {
          return (
            <ControlsContainer>
              <Tag.CheckableTag
                checked={row.startIdx === selectedRsfStartIdx}
                onChange={(checked) =>
                  setSelectedRsfStartIdx(checked ? row.startIdx : undefined)
                }>
                <EyeOutlined />
              </Tag.CheckableTag>
            </ControlsContainer>
          )
        },
      },
    ]
  }, [minThicknessRsf, selectedRsfStartIdx])

  const rankedRsfs = useMemo(
    () => rsfs.map((rsf, idx) => ({ ...rsf, rank: idx + 1 })),
    [rsfs]
  )

  const selectedRsfSlices = useMemo(
    () =>
      rsfs.find((rsf) => rsf.startIdx === selectedRsfStartIdx)?.slices ?? [],
    [rsfs, selectedRsfStartIdx]
  )
  const closeModal = useCallback(() => {
    setSelectedRsfStartIdx(undefined)
  }, [])

  return (
    <>
      <Modal
        open={!!selectedRsfStartIdx}
        footer={null}
        onCancel={closeModal}
        title={`Slices for RSF ${selectedRsfStartIdx}`}
        width='1000px'>
        <CtpSliceTable slices={selectedRsfSlices} />
      </Modal>
      <Table
        columns={columns}
        dataSource={rankedRsfs}
        rowKey={(rsfRec) => rsfRec.startIdx}
      />
    </>
  )
}
